import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { SetDetail } from '../trade/tradeApi';

export type TradeAlertsTab = 'all' | 'hidden';
export type TradeAlertsFilter = 'all' | 'high-value';
export type TradeAlertsSort = 'newest' | 'oldest' | 'highest-value' | 'lowest-value';
export type TradeAlertsAlertStatus = 'all' | 'on' | 'off';

export interface TradeAlertCard {
  id: number;
  name: string;
  setCode: string;
  collectorNumber: string;
  imageUrl: string;
  foil: boolean;
  language: string; // lowercase Scryfall code (e.g. "en")
  condition: string; // e.g. "NEAR_MINT"
  scryfallId?: string;
  /**
   * Wanter acceptance criteria — what printing/finish/language/condition the requester will accept.
   * Same shapes the Send page's available-packages exposes. All optional → rendered defensively
   * (overlay/tooltip/version-flyout degrade to "Any …" / single printing when absent).
   * `acceptableConditions`/`acceptableLanguages` are omitted by the backend when empty.
   */
  acceptableSetDetails?: SetDetail[];
  acceptableConditions?: string[];
  acceptableLanguages?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;
}

export interface TradeAlertRequester {
  id: number;
  username: string;
  avatarUrl?: string | null;
  /**
   * Avatar-parity + recipient-balance fields. Optional so the page degrades
   * gracefully against the current production backend during the FE-first
   * rollout window; always present once the backend branch
   * (eric/trade-alerts-price-filter-requester-enrich) deploys.
   */
  points?: number;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
  country?: string | null;
}

export interface TradeAlert {
  id: string; // "{haveListItemId}-{wantListItemId}"
  haveListItemId: number;
  wantListItemId: number;
  card: TradeAlertCard;
  requester: TradeAlertRequester;
  pointValue: number;
  availableSince: string; // ISO date string
  hidden: boolean;
  /** Whether the matched Have item has SMS alerts enabled. */
  alertsEnabled: boolean;
}

export interface GetTradeAlertsRequest {
  tab?: TradeAlertsTab;
  /**
   * @deprecated Replaced by `priceMin`/`priceMax`. The FE no longer sends this;
   * the backend keeps a `high-value → priceMin=500` alias for a ~1-week
   * transition window, after which this field is removed (cleanup ticket).
   */
  filter?: TradeAlertsFilter;
  sort?: TradeAlertsSort;
  alertStatus?: TradeAlertsAlertStatus;
  /** Inclusive lower bound on card pointValue (non-negative integer). */
  priceMin?: number;
  /** Inclusive upper bound on card pointValue (non-negative integer). */
  priceMax?: number;
}

export interface TradeAlertsCounts {
  all: number;
  on: number;
  off: number;
}

export interface GetTradeAlertsResponse {
  alerts: TradeAlert[];
  allCount: number;
  hiddenCount: number;
  /**
   * Per-status counts for the three alert-status pills. Reflect the active tab +
   * high-value filters but are independent of the selected alertStatus.
   */
  counts: TradeAlertsCounts;
}

export interface HideTradeAlertRequest {
  haveListItemId: number;
  wantListItemId: number;
}

export interface HideTradeAlertResponse {
  alert: TradeAlert;
}

export const tradeAlertsApi = createApi({
  reducerPath: 'tradeAlertsApi',
  baseQuery: baseQuery,
  tagTypes: ['TradeAlerts'],
  endpoints: (builder) => ({
    getTradeAlerts: builder.query<GetTradeAlertsResponse, GetTradeAlertsRequest | void>({
      query: (params) => {
        const { tab = 'all', sort = 'newest', alertStatus = 'on', priceMin, priceMax } =
          params || {};
        const search = new URLSearchParams({ tab, sort, alertStatus });
        // Price range replaces the deprecated `filter=high-value`. Only append
        // bounds when set so the default (no filtering) sends neither param.
        if (priceMin != null) search.set('priceMin', String(priceMin));
        if (priceMax != null) search.set('priceMax', String(priceMax));
        return {
          url: `/trade-alerts?${search.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['TradeAlerts'],
    }),

    hideTradeAlert: builder.mutation<HideTradeAlertResponse, HideTradeAlertRequest>({
      query: (body) => ({
        url: '/trade-alerts/hide',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradeAlerts'],
    }),

    unhideTradeAlert: builder.mutation<HideTradeAlertResponse, HideTradeAlertRequest>({
      query: (body) => ({
        url: '/trade-alerts/unhide',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradeAlerts'],
    }),
  }),
});

export const {
  useGetTradeAlertsQuery,
  useHideTradeAlertMutation,
  useUnhideTradeAlertMutation,
} = tradeAlertsApi;
